import axios from 'axios';
import {
  CREATE_FACIAL_VERIFICATION_SESSION_URL,
  FACIAL_VERIFICATION_HEALTH_URL,
  FACIAL_VERIFICATION_RESULT_URL,
  get_auth_headers,
  get_request_config,
  IDENTITY_FACIAL_VERIFICATION_API_URL,
  UPLOAD_FACIAL_VERIFICATION_ID_DOCUMENT_URL,
  VERIFY_FACIAL_VERIFICATION_SESSION_URL
} from '@/utils/system_constants/constant.js';

const FACIAL_VERIFICATION_SESSION_ID_PATTERN = /^[A-Za-z0-9_-]+$/u;

const facial_verification_client = axios.create({
  baseURL: IDENTITY_FACIAL_VERIFICATION_API_URL,
  withCredentials: true
});

function normalize_session_id(session_id = '') {
  const normalized_session_id = String(session_id || '').trim();
  if (!normalized_session_id || !FACIAL_VERIFICATION_SESSION_ID_PATTERN.test(normalized_session_id)) {
    throw new Error('Invalid facial verification session id.');
  }
  return normalized_session_id;
}

function build_session_endpoint(template_url = '', session_id = '') {
  const safe_session_id = normalize_session_id(session_id);
  if (!String(template_url).includes(':session_id')) {
    throw new Error('Invalid facial verification session URL template.');
  }

  return String(template_url).replace(':session_id', encodeURIComponent(safe_session_id));
}

function get_relative_session_path(template_url = '', session_id = '') {
  const absolute_endpoint = build_session_endpoint(template_url, session_id);
  if (!absolute_endpoint.startsWith(IDENTITY_FACIAL_VERIFICATION_API_URL)) {
    throw new Error('Invalid facial verification session endpoint.');
  }

  return absolute_endpoint.slice(IDENTITY_FACIAL_VERIFICATION_API_URL.length);
}

export const fetch_facial_verification_health = async () => {
  const response = await axios.get(FACIAL_VERIFICATION_HEALTH_URL, get_request_config());
  return response?.data || {};
};

export const create_facial_verification_session = async ({
  application_form_id,
  section_question_id,
  journey_reference = 'onboarding-intake'
} = {}) => {
  const response = await axios.post(
    CREATE_FACIAL_VERIFICATION_SESSION_URL,
    {
      application_form_id,
      section_question_id,
      journey_reference
    },
    get_request_config()
  );
  return response?.data || {};
};

export const upload_facial_verification_id_document = async ({
  session_id,
  session_token,
  document
} = {}) => {
  const form_data = new FormData();
  form_data.append('document', document);
  form_data.append('session_token', String(session_token || '').trim());
  const response = await facial_verification_client.post(
    get_relative_session_path(UPLOAD_FACIAL_VERIFICATION_ID_DOCUMENT_URL, session_id),
    form_data,
    {
    headers: {
      ...get_auth_headers()
    }
    }
  );
  return response?.data || {};
};

export const verify_facial_verification_session = async ({
  session_id,
  session_token,
  frames = [],
  client = {}
} = {}) => {
  const response = await facial_verification_client.post(
    get_relative_session_path(VERIFY_FACIAL_VERIFICATION_SESSION_URL, session_id),
    {
      session_token: String(session_token || '').trim(),
      frames,
      client
    },
    {
      headers: {
        ...get_auth_headers(),
        'Content-Type': 'application/json'
      }
    }
  );
  return response?.data || {};
};

export const fetch_facial_verification_result = async ({
  application_form_id,
  section_question_id
} = {}) => {
  const response = await axios.get(
    FACIAL_VERIFICATION_RESULT_URL,
    get_request_config({
      params: {
        application_form_id,
        section_question_id
      }
    })
  );
  return response?.data || {};
};
